

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  UserRound,
  BriefcaseBusiness,
  FileText,
  CalendarDays,
  CheckCircle2,
  Clock3,
  XCircle,
  IndianRupee,
  MapPin,
  Mail,
  Phone,
  Award,
  TrendingUp,
  ArrowUpRight,
  ChevronRight,
  RefreshCw,
  Search,
  Bell,
  ShieldCheck,
  Target,
  Users,
} from "lucide-react";

export default function TrainerDashboard() {
  const navigate = useNavigate();

  const [trainer, setTrainer] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
// const navigate = useNavigate();

const goToInterviews = () => {
  navigate("/trainer/interviews");
};
  const TRAINERS_API =
    "/api/trainers/all";

  const APPLICATIONS_API =
    "/api/job-applications";

  /* =====================================================
     AUTH
  ===================================================== */

  const getUser = () => {
    const storedUser = localStorage.getItem("user");

    if (!storedUser) return null;

    try {
      return JSON.parse(storedUser);
    } catch (error) {
      console.error("User parse error:", error);
      return null;
    }
  };

  const getHeaders = () => {
    const token = localStorage.getItem("token");

    return {
      "Content-Type": "application/json",
      ...(token
        ? {
          Authorization: `Bearer ${token}`,
        }
        : {}),
    };
  };

  /* =====================================================
     ARRAY NORMALIZER
  ===================================================== */

  const getArray = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.content)) return data.content;
    if (Array.isArray(data?.results)) return data.results;

    return [];
  };

  /* =====================================================
     GET TRAINER PROFILE
  ===================================================== */

  const getTrainer = async () => {
    const user = getUser();

    if (!user) {
      throw new Error("Trainer login required.");
    }

    /* First preference: trainer ID */

    const storedTrainerId =
      user?.trainerId ||
      user?.trainer?.id ||
      localStorage.getItem("trainerId");

    if (storedTrainerId) {
      const trainersResponse = await fetch(
        TRAINERS_API,
        {
          headers: getHeaders(),
        }
      );

      if (trainersResponse.ok) {
        const data = await trainersResponse.json();
        const trainers = getArray(data);

        const matchedById = trainers.find(
          (item) =>
            Number(item?.id) ===
            Number(storedTrainerId)
        );

        if (matchedById) {
          localStorage.setItem(
            "trainerId",
            String(matchedById.id)
          );

          return matchedById;
        }
      }
    }

    /* Fallback: email */

    if (!user?.email) {
      throw new Error(
        "Trainer email not found."
      );
    }

    const response = await fetch(
      TRAINERS_API,
      {
        headers: getHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error(
        "Unable to fetch trainer profile."
      );
    }

    const data = await response.json();
    const trainers = getArray(data);

    const matchedTrainer = trainers.find(
      (item) =>
        item?.email
          ?.trim()
          .toLowerCase() ===
        user.email
          .trim()
          .toLowerCase()
    );

    if (!matchedTrainer) {
      throw new Error(
        "Trainer profile not found."
      );
    }

    localStorage.setItem(
      "trainerId",
      String(matchedTrainer.id)
    );

    return matchedTrainer;
  };

  /* =====================================================
     LOAD DASHBOARD
  ===================================================== */

  const loadDashboard = async () => {
    try {
      setLoading(true);

      const currentTrainer =
        await getTrainer();

      setTrainer(currentTrainer);

      const trainerId =
        Number(currentTrainer?.id);

      if (!trainerId) {
        setApplications([]);
        return;
      }

      const response = await fetch(
        `${APPLICATIONS_API}/trainer/${trainerId}`,
        {
          headers: getHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(
          "Unable to fetch applications."
        );
      }

      const data = await response.json();

      const result = getArray(data);

      /* Only current trainer applications */

      const ownApplications =
        result.filter((application) => {
          const applicationTrainerId =
            Number(
              application?.trainerId ??
              application?.trainer?.id ??
              application?.trainer?.trainerId
            );

          return (
            applicationTrainerId ===
            trainerId
          );
        });

      setApplications(
        ownApplications
      );
    } catch (error) {
      console.error(
        "Dashboard error:",
        error
      );

      setTrainer(null);
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  /* =====================================================
     HELPERS
  ===================================================== */

  const normalizeStatus = (status) =>
    String(status || "PENDING")
      .trim()
      .toUpperCase();

  const formatDate = (date) => {
    if (!date) return "-";

    const parsed = new Date(date);

    if (Number.isNaN(parsed.getTime())) {
      return "-";
    }

    return parsed.toLocaleDateString(
      "en-IN",
      {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }
    );
  };

  const getInitials = (name) => {
    if (!name) return "TR";

    return name
      .split(" ")
      .slice(0, 2)
      .map((item) =>
        item.charAt(0)
      )
      .join("")
      .toUpperCase();
  };

  const getSkills = () => {
    if (!trainer?.skills) return [];

    if (Array.isArray(trainer.skills)) {
      return trainer.skills;
    }

    return String(trainer.skills)
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean)
      .slice(0, 6);
  };

  /* =====================================================
     STATISTICS
  ===================================================== */

  const stats = useMemo(() => {
    const result = {
      total: applications.length,
      pending: 0,
      shortlisted: 0,
      interview: 0,
      selected: 0,
      rejected: 0,
    };

    applications.forEach(
      (application) => {
        const status =
          normalizeStatus(
            application?.applicationStatus
          );

        if (status === "PENDING") {
          result.pending++;
        }

        if (
          status === "SHORTLISTED"
        ) {
          result.shortlisted++;
        }

        if (
          status === "INTERVIEW" ||
          status ===
          "INTERVIEW_SCHEDULED"
        ) {
          result.interview++;
        }

        if (
          status === "SELECTED"
        ) {
          result.selected++;
        }

        if (
          status === "REJECTED"
        ) {
          result.rejected++;
        }
      }
    );

    return result;
  }, [applications]);

  /* =====================================================
     RECENT APPLICATIONS
  ===================================================== */

  const recentApplications =
    useMemo(() => {
      return [...applications]
        .sort((a, b) => {
          const dateA =
            new Date(
              a?.appliedAt ||
              a?.createdAt ||
              0
            ).getTime();

          const dateB =
            new Date(
              b?.appliedAt ||
              b?.createdAt ||
              0
            ).getTime();

          return dateB - dateA;
        })
        .slice(0, 5);
    }, [applications]);

  /* =====================================================
     STATUS CLASS
  ===================================================== */

  const getStatusClass = (status) => {
    switch (
    normalizeStatus(status)
    ) {
      case "SELECTED":
        return "status selected";

      case "SHORTLISTED":
        return "status shortlisted";

      case "INTERVIEW":
      case "INTERVIEW_SCHEDULED":
        return "status interview";

      case "REJECTED":
        return "status rejected";

      default:
        return "status pending";
    }
  };

  /* =====================================================
     NAVIGATION
  ===================================================== */

const goToProfile = () => {
  if (trainer?.id) {
    navigate(`/trainer/${trainer.id}`);
  }
};

  const goToApplications = () => {
    navigate(
      "/trainer/applications"
    );
  };

  const goToJobs = () => {
    navigate(
      "/trainer/available-jobs"
    );
  };

  const goToApplication = (
    application
  ) => {
    if (!application?.applicationId) {
      return;
    }

    navigate(
      `/trainer/applications/${application.applicationId}`
    );
  };

  /* =====================================================
     LOADING
  ===================================================== */

  if (loading) {
    return (
      <>
        <style>{dashboardCSS}</style>

        <div className="trainer-dashboard-loading">
          <div className="dashboard-loader">
            <RefreshCw
              size={27}
              className="spin"
            />
          </div>

          <h3>
            Loading Trainer Dashboard
          </h3>

          <p>
            Preparing your profile,
            applications and opportunities...
          </p>
        </div>
      </>
    );
  }

  /* =====================================================
     DASHBOARD
  ===================================================== */

  return (
    <>
      <style>{dashboardCSS}</style>

      <div className="trainer-dashboard">

        {/* =================================================
            HEADER
        ================================================= */}

        <header className="dashboard-header">

          <div className="header-left">

            <div className="dashboard-icon">
              <LayoutDashboard
                size={21}
              />
            </div>

            <div>
              <div className="breadcrumb">
                Trainer Portal
                <ChevronRight
                  size={12}
                />
                Dashboard
              </div>

              <h1>
                Trainer Dashboard
              </h1>

              <p>
                Manage your training
                profile, applications and
                opportunities.
              </p>
            </div>

          </div>

          <div className="header-actions">

            <button
              className="icon-btn"
              title="Refresh"
              onClick={loadDashboard}
            >
              <RefreshCw
                size={16}
              />
            </button>

            {/* <button
              className="icon-btn"
              title="Notifications"
              onClick={() =>
                navigate(
                  "/trainer/notifications"
                )
              }
            >
              <Bell size={16} />
            </button>

            <button
              className="profile-mini"
              onClick={
                goToProfile
              }
            >
              <span>
                {getInitials(
                  trainer?.name
                )}
              </span>

              <div>
                <strong>
                  {trainer?.name ||
                    "Trainer"}
                </strong>

                <small>
                  Trainer
                </small>
              </div>

              <ChevronRight
                size={14}
              />
            </button> */}

          </div>

        </header>

        {/* =================================================
            PROFILE HERO
        ================================================= */}

        <section className="trainer-hero">

          <div className="hero-profile">

            <div className="hero-avatar">
              {getInitials(
                trainer?.name
              )}
            </div>

            <div className="hero-info">

              <div className="hero-name-row">

                <h2>
                  Welcome back,{" "}
                  {trainer?.name ||
                    "Trainer"}
                  !
                </h2>

                <span className="verified">
                  <ShieldCheck
                    size={12}
                  />
                  Verified Profile
                </span>

              </div>

              <div className="hero-details">

                {trainer?.email && (
                  <span>
                    <Mail size={13} />
                    {trainer.email}
                  </span>
                )}

                {(trainer?.city ||
                  trainer?.state) && (
                    <span>
                      <MapPin
                        size={13}
                      />

                      {[
                        trainer.city,
                        trainer.state,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </span>
                  )}

                {trainer?.experience && (
                  <span>
                    <Award size={13} />
                    {trainer.experience}{" "}
                    Experience
                  </span>
                )}

              </div>

              <div className="hero-skills">

                {getSkills().length >
                  0 ? (
                  getSkills().map(
                    (skill, index) => (
                      <span
                        key={
                          `${skill}-${index}`
                        }
                      >
                        {skill}
                      </span>
                    )
                  )
                ) : (
                  <span>
                    Add your skills to
                    attract better
                    opportunities
                  </span>
                )}

              </div>

            </div>

          </div>

          <button
            className="hero-profile-btn"
            onClick={
              goToProfile
            }
          >
            View My Profile
            <ArrowUpRight
              size={15}
            />
          </button>

        </section>

        {/* =================================================
            STAT CARDS
        ================================================= */}

        <section className="dashboard-stats">

          <StatCard
            icon={
              <BriefcaseBusiness
                size={19}
              />
            }
            label="Total Applications"
            value={stats.total}
            className="blue"
            onClick={
              goToApplications
            }
          />

          <StatCard
            icon={
              <Clock3 size={19} />
            }
            label="Pending Applications"
            value={stats.pending}
            className="orange"
            onClick={
              goToApplications
            }
          />

          <StatCard
            icon={
              <Award size={19} />
            }
            label="Shortlisted"
            value={
              stats.shortlisted
            }
            className="purple"
            onClick={
              goToApplications
            }
          />

          <StatCard
            icon={
              <CalendarDays
                size={19}
              />
            }
            label="Interviews"
            value={stats.interview}
            className="violet"
            onClick={
              goToApplications
            }
          />

          <StatCard
            icon={
              <CheckCircle2
                size={19}
              />
            }
            label="Selected"
            value={stats.selected}
            className="green"
            onClick={
              goToApplications
            }
          />

        </section>

        {/* =================================================
            MAIN GRID
        ================================================= */}

        <div className="dashboard-main-grid">

          {/* ===============================================
              RECENT APPLICATIONS
          =============================================== */}

          <section className="dashboard-panel applications-panel">

            <div className="panel-header">

              <div>
                <h2>
                  Recent Applications
                </h2>

                <p>
                  Latest applications
                  submitted from your
                  trainer profile.
                </p>
              </div>

              <button
                onClick={
                  goToApplications
                }
              >
                View All
                <ChevronRight
                  size={14}
                />
              </button>

            </div>

            {recentApplications.length >
              0 ? (
              <div className="recent-applications">

                {recentApplications.map(
                  (application, index) => {

                    const jobTitle =
                      application?.jobTitle ||
                      application?.job?.jobTitle ||
                      application?.title ||
                      `Job #${application?.jobId ||
                      "-"
                      }`;

                    const location =
                      application?.location ||
                      application?.job?.location ||
                      "Location not specified";

                    const status =
                      normalizeStatus(
                        application?.applicationStatus
                      );

                    const appliedDate =
                      application?.appliedAt ||
                      application?.createdAt;

                    return (
                      <div
                        className="recent-application"
                        key={
                          application?.applicationId ||
                          index
                        }
                        onClick={() =>
                          goToApplication(
                            application
                          )
                        }
                      >

                        <div className="recent-job-icon">
                          <BriefcaseBusiness
                            size={16}
                          />
                        </div>

                        <div className="recent-job-info">

                          <h3>
                            {jobTitle}
                          </h3>

                          <div className="recent-job-meta">

                            <span>
                              <MapPin
                                size={11}
                              />
                              {location}
                            </span>

                            <span>
                              <CalendarDays
                                size={11}
                              />
                              {formatDate(
                                appliedDate
                              )}
                            </span>

                          </div>

                        </div>

                        <span
                          className={getStatusClass(
                            status
                          )}
                        >
                          {status ===
                            "SELECTED" ? (
                            <CheckCircle2
                              size={12}
                            />
                          ) : status ===
                            "REJECTED" ? (
                            <XCircle
                              size={12}
                            />
                          ) : (
                            <Clock3
                              size={12}
                            />
                          )}

                          {status}
                        </span>

                        <ChevronRight
                          size={15}
                          className="recent-arrow"
                        />

                      </div>
                    );
                  }
                )}

              </div>
            ) : (
              <EmptyApplications
                onBrowse={
                  goToJobs
                }
              />
            )}

          </section>

          {/* ===============================================
              QUICK ACTIONS
          =============================================== */}

          <section className="dashboard-panel quick-panel">

            <div className="panel-header">

              <div>
                <h2>
                  Quick Actions
                </h2>

                <p>
                  Manage your trainer
                  activities.
                </p>
              </div>

            </div>

            <div className="quick-actions">

              <QuickAction
                icon={
                  <Search size={18} />
                }
                title="Find New Jobs"
                description="Explore available training opportunities"
                onClick={
                  goToJobs
                }
              />
              <QuickAction
                icon={<Bell size={18} />}
                title="Invitations Received"
                description="View invitations from partners"
                onClick={() => navigate("/trainer-notifications")}
              />

              <QuickAction
                icon={
                  <FileText
                    size={18}
                  />
                }
                title="My Applications"
                description="Track your submitted applications"
                onClick={
                  goToApplications
                }
              />

              <QuickAction
                icon={
                  <UserRound
                    size={18}
                  />
                }
                title="My Profile"
                description="View and manage your trainer profile"
                onClick={
                  goToProfile
                }
              />

              <QuickAction
  icon={<CalendarDays size={18} />}
  title="Interviews"
  description="Check your interview schedule"
  onClick={goToInterviews}
/>

            </div>

          </section>

        </div>

        {/* =================================================
            PROFILE COMPLETION
        ================================================= */}

        <section className="dashboard-panel profile-completion">

          <div className="completion-left">

            <div className="completion-icon">
              <Target size={20} />
            </div>

            <div>

              <h2>
                Complete Your Trainer
                Profile
              </h2>

              <p>
                A complete profile can
                improve your chances of
                getting shortlisted for
                relevant training
                opportunities.
              </p>

            </div>

          </div>

          <div className="completion-right">

            <div className="progress-wrapper">

              <div className="progress-label">
                <span>
                  Profile Strength
                </span>

                <strong>
                  {trainer
                    ? calculateProfileCompletion(
                      trainer
                    )
                    : 0}
                  %
                </strong>
              </div>

              <div className="progress-bar">
                <span
                  style={{
                    width: `${trainer
                      ? calculateProfileCompletion(
                        trainer
                      )
                      : 0
                      }%`,
                  }}
                />
              </div>

            </div>

            <button
              onClick={
                goToProfile
              }
            >
              Update Profile
              <ChevronRight
                size={14}
              />
            </button>

          </div>

        </section>

        {/* =================================================
            FOOTER INFO
        ================================================= */}

        <div className="dashboard-footer-note">

          <ShieldCheck size={15} />

          <span>
            Your dashboard is connected
            to your trainer profile.
            Application data shown here
            belongs only to your account.
          </span>

        </div>

      </div>
    </>
  );
}

/* =========================================================
   STAT CARD
========================================================= */

function StatCard({
  icon,
  label,
  value,
  className,
  onClick,
}) {
  return (
    <button
      className={`dashboard-stat-card ${className}`}
      onClick={onClick}
    >
      <div className="stat-card-icon">
        {icon}
      </div>

      <div className="stat-card-content">
        <span>
          {label}
        </span>

        <strong>
          {value}
        </strong>
      </div>

      <ArrowUpRight
        size={14}
        className="stat-arrow"
      />
    </button>
  );
}

/* =========================================================
   QUICK ACTION
========================================================= */

function QuickAction({
  icon,
  title,
  description,
  onClick,
}) {
  return (
    <button
      className="quick-action"
      onClick={onClick}
    >
      <div className="quick-action-icon">
        {icon}
      </div>

      <div className="quick-action-content">
        <strong>
          {title}
        </strong>

        <span>
          {description}
        </span>
      </div>

      <ChevronRight
        size={15}
      />
    </button>
  );
}

/* =========================================================
   EMPTY APPLICATIONS
========================================================= */

function EmptyApplications({
  onBrowse,
}) {
  return (
    <div className="empty-applications">

      <div className="empty-app-icon">
        <BriefcaseBusiness
          size={26}
        />
      </div>

      <h3>
        No Applications Yet
      </h3>

      <p>
        Start exploring available
        trainer opportunities and
        apply for suitable jobs.
      </p>

      <button
        onClick={onBrowse}
      >
        Browse Jobs
        <ChevronRight
          size={14}
        />
      </button>

    </div>
  );
}

/* =========================================================
   PROFILE COMPLETION
========================================================= */

function calculateProfileCompletion(
  trainer
) {
  if (!trainer) return 0;

  const fields = [
    trainer.name,
    trainer.email,
    trainer.phone,
    trainer.experience,
    trainer.skills,
    trainer.state,
    trainer.city,
    trainer.pincode,
    trainer.resumeName,
  ];

  const completed =
    fields.filter(
      (field) =>
        field !== null &&
        field !== undefined &&
        String(field).trim() !== ""
    ).length;

  return Math.round(
    (completed /
      fields.length) *
    100
  );
}

/* =========================================================
   CSS
========================================================= */
const dashboardCSS = `

/* ========================================================
   CERTED BRAND COLORS
======================================================== */

:root {
  --certed-primary: #3E0F8D;
  --certed-secondary: #9564DD;
  --certed-accent: #E4DA72;
  --certed-light: #EEEEEE;

  --certed-primary-dark: #2C0868;
  --certed-primary-soft: #F4EFFA;
  --certed-secondary-soft: #F7F1FC;

  --text-primary: #251A35;
  --text-secondary: #665D72;
  --text-muted: #8E8799;

  --white: #FFFFFF;
  --border: #E6E0EC;

  --success: #16814C;
  --success-bg: #EBF8F1;

  --danger: #B83232;
  --danger-bg: #FFF0F0;

  --warning: #9B7600;
  --warning-bg: #FFF9DF;
}

/* ========================================================
   BASE
======================================================== */

.trainer-dashboard {
  min-height: 100vh;
  padding: 26px;
  box-sizing: border-box;
margin-top: 70px;
  background:
    radial-gradient(
      circle at top right,
      rgba(149, 100, 221, .08),
      transparent 30%
    ),
    linear-gradient(
      180deg,
      #FFFFFF 0%,
      #EEEEEE 100%
    );

  color: var(--text-primary);

  font-family:
    Inter,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;

  font-size: 13px;
}

/* ========================================================
   HEADER
======================================================== */

.dashboard-header {
  max-width: 1500px;
  margin: 0 auto 22px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 20px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.dashboard-icon {
  width: 45px;
  height: 45px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 12px;

  background:
    linear-gradient(
      135deg,
      var(--certed-primary),
      var(--certed-secondary)
    );

  color: #FFFFFF;

  box-shadow:
    0 8px 20px
    rgba(62, 15, 141, .18);
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 4px;

  margin-bottom: 5px;

  color: var(--text-muted);

  font-size: 11px;
  font-weight: 700;
}

.dashboard-header h1 {
  margin: 0;

  color: var(--certed-primary);

  font-size: 24px;
  line-height: 1.15;

  font-weight: 800;

  letter-spacing: -.035em;
}

.dashboard-header p {
  margin: 5px 0 0;

  color: var(--text-muted);

  font-size: 12px;
}

/* ========================================================
   HEADER ACTIONS
======================================================== */

.header-actions {
  display: flex;
  align-items: center;
  gap: 7px;
}

.icon-btn {
  width: 36px;
  height: 36px;

  display: flex;
  align-items: center;
  justify-content: center;

  border: 1px solid var(--border);
  border-radius: 8px;

  background: var(--white);

  color: var(--text-secondary);

  cursor: pointer;

  transition: .2s ease;
}

.icon-btn:hover {
  color: var(--certed-primary);

  border-color:
    rgba(149, 100, 221, .45);

  background:
    var(--certed-primary-soft);

  transform: translateY(-1px);
}

.profile-mini {
  height: 40px;

  display: flex;
  align-items: center;

  gap: 8px;

  padding: 0 10px;

  border: 1px solid var(--border);
  border-radius: 9px;

  background: var(--white);

  cursor: pointer;

  transition: .2s ease;
}

.profile-mini:hover {
  border-color:
    rgba(149, 100, 221, .45);

  box-shadow:
    0 6px 18px
    rgba(62, 15, 141, .08);
}

.profile-mini > span {
  width: 29px;
  height: 29px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 7px;

  background:
    linear-gradient(
      135deg,
      var(--certed-primary),
      var(--certed-secondary)
    );

  color: #FFFFFF;

  font-size: 10px;
  font-weight: 800;
}

.profile-mini div {
  text-align: left;
}

.profile-mini strong {
  display: block;

  color: var(--text-primary);

  font-size: 11px;
  font-weight: 700;
}

.profile-mini small {
  display: block;

  margin-top: 1px;

  color: var(--text-muted);

  font-size: 9px;
}

/* ========================================================
   HERO
======================================================== */

.trainer-hero {
  max-width: 1500px;

  margin: 0 auto 18px;

  padding: 19px 20px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 20px;

  border: 1px solid
    rgba(149, 100, 221, .20);

  border-radius: 15px;

  background:
    linear-gradient(
      135deg,
      #FFFFFF 0%,
      #FAF7FD 55%,
      #F4EFFA 100%
    );

  box-shadow:
    0 9px 28px
    rgba(62, 15, 141, .07);

  position: relative;
  overflow: hidden;
}

.trainer-hero::after {
  content: "";

  position: absolute;

  width: 180px;
  height: 180px;

  right: -80px;
  top: -90px;

  border-radius: 50%;

  background:
    rgba(228, 218, 114, .20);

  pointer-events: none;
}

.hero-profile {
  min-width: 0;

  display: flex;
  align-items: center;

  gap: 14px;

  position: relative;
  z-index: 1;
}

.hero-avatar {
  width: 65px;
  height: 65px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 17px;

  background:
    linear-gradient(
      135deg,
      var(--certed-primary),
      var(--certed-secondary)
    );

  color: #FFFFFF;

  font-size: 19px;
  font-weight: 800;

  box-shadow:
    0 10px 24px
    rgba(62, 15, 141, .22);
}

.hero-info {
  min-width: 0;
}

.hero-name-row {
  display: flex;
  align-items: center;

  flex-wrap: wrap;

  gap: 8px;
}

.hero-name-row h2 {
  margin: 0;

  color: var(--certed-primary);

  font-size: 19px;
  font-weight: 800;
}

.verified {
  display: inline-flex;
  align-items: center;

  gap: 4px;

  padding: 5px 8px;

  border-radius: 20px;

  background:
    rgba(228, 218, 114, .35);

  color:
    var(--certed-primary);

  font-size: 9px;
  font-weight: 800;

  text-transform: uppercase;

  border: 1px solid
    rgba(228, 218, 114, .65);
}

.hero-details {
  margin-top: 7px;

  display: flex;
  align-items: center;

  flex-wrap: wrap;

  gap: 12px;
}

.hero-details span {
  display: flex;
  align-items: center;

  gap: 4px;

  color: var(--text-secondary);

  font-size: 11px;
}

.hero-details svg {
  color: var(--certed-secondary);
}

.hero-skills {
  margin-top: 8px;

  display: flex;
  flex-wrap: wrap;

  gap: 5px;
}

.hero-skills span {
  padding: 5px 8px;

  border: 1px solid
    rgba(149, 100, 221, .20);

  border-radius: 5px;

  background: #FFFFFF;

  color: var(--certed-primary);

  font-size: 9px;
  font-weight: 700;
}

.hero-skills span:hover {
  background:
    var(--certed-primary-soft);

  border-color:
    var(--certed-secondary);
}

.hero-profile-btn {
  height: 37px;

  flex-shrink: 0;

  display: inline-flex;
  align-items: center;

  gap: 5px;

  padding: 0 13px;

  border: 1px solid
    var(--certed-secondary);

  border-radius: 8px;

  background:
    var(--certed-primary);

  color: #FFFFFF;

  font-size: 11px;
  font-weight: 800;

  cursor: pointer;

  transition: .2s ease;

  position: relative;
  z-index: 2;
}

.hero-profile-btn:hover {
  background:
    var(--certed-secondary);

  border-color:
    var(--certed-secondary);

  transform:
    translateY(-1px);

  box-shadow:
    0 7px 18px
    rgba(62, 15, 141, .18);
}

/* ========================================================
   STATS
======================================================== */

.dashboard-stats {
  max-width: 1500px;

  margin: 0 auto 19px;

  display: grid;

  grid-template-columns:
    repeat(5, minmax(0, 1fr));

  gap: 11px;
}

.dashboard-stat-card {
  position: relative;

  min-width: 0;

  padding: 14px;

  display: flex;
  align-items: center;

  gap: 9px;

  border: 1px solid var(--border);

  border-radius: 11px;

  background: #FFFFFF;

  text-align: left;

  cursor: pointer;

  transition: .2s ease;
}

.dashboard-stat-card:hover {
  transform:
    translateY(-2px);

  border-color:
    rgba(149, 100, 221, .40);

  box-shadow:
    0 10px 25px
    rgba(62, 15, 141, .08);
}

.stat-card-icon {
  width: 36px;
  height: 36px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 9px;

  background:
    var(--certed-primary-soft);

  color:
    var(--certed-primary);
}

.blue .stat-card-icon {
  background:
    var(--certed-primary-soft);

  color:
    var(--certed-primary);
}

.orange .stat-card-icon {
  background:
    rgba(228, 218, 114, .28);

  color:
    #756300;
}

.purple .stat-card-icon {
  background:
    rgba(149, 100, 221, .13);

  color:
    var(--certed-secondary);
}

.violet .stat-card-icon {
  background:
    #F0E7FA;

  color:
    #6D3EB2;
}

.green .stat-card-icon {
  background:
    var(--success-bg);

  color:
    var(--success);
}

.stat-card-content span {
  display: block;

  color: var(--text-muted);

  font-size: 10px;
  font-weight: 600;
}

.stat-card-content strong {
  display: block;

  margin-top: 2px;

  color: var(--certed-primary);

  font-size: 21px;

  font-weight: 800;
}

.stat-arrow {
  position: absolute;

  top: 10px;
  right: 10px;

  color:
    rgba(62, 15, 141, .30);

  transition: .2s ease;
}

.dashboard-stat-card:hover .stat-arrow {
  color:
    var(--certed-secondary);

  transform:
    translate(2px, -2px);
}

/* ========================================================
   MAIN GRID
======================================================== */

.dashboard-main-grid {
  max-width: 1500px;

  margin: 0 auto 18px;

  display: grid;

  grid-template-columns:
    minmax(0, 1.8fr)
    minmax(300px, .9fr);

  gap: 16px;
}

.dashboard-panel {
  min-width: 0;

  padding: 18px;

  border: 1px solid var(--border);

  border-radius: 13px;

  background:
    #FFFFFF;

  box-shadow:
    0 5px 20px
    rgba(62, 15, 141, .035);
}

/* ========================================================
   PANEL HEADER
======================================================== */

.panel-header {
  display: flex;

  align-items: flex-start;

  justify-content: space-between;

  gap: 15px;

  margin-bottom: 13px;
}

.panel-header h2 {
  margin: 0;

  color:
    var(--certed-primary);

  font-size: 16px;

  font-weight: 800;
}

.panel-header p {
  margin: 4px 0 0;

  color:
    var(--text-muted);

  font-size: 11px;
}

.panel-header button {
  display: inline-flex;

  align-items: center;

  gap: 3px;

  border: 0;

  background: transparent;

  color:
    var(--certed-secondary);

  font-size: 10px;

  font-weight: 800;

  cursor: pointer;
}

.panel-header button:hover {
  color:
    var(--certed-primary);
}

/* ========================================================
   RECENT APPLICATIONS
======================================================== */

.recent-applications {
  display: flex;
  flex-direction: column;
}

.recent-application {
  min-width: 0;

  padding: 12px 3px;

  display: flex;
  align-items: center;

  gap: 10px;

  border-top:
    1px solid #F0EDF3;

  cursor: pointer;

  transition: .15s ease;
}

.recent-application:first-child {
  border-top: 0;
}

.recent-application:hover {
  padding-left: 6px;
  padding-right: 0;

  background:
    #FCFAFE;
}

.recent-job-icon {
  width: 36px;
  height: 36px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 8px;

  background:
    var(--certed-primary-soft);

  color:
    var(--certed-primary);
}

.recent-job-info {
  min-width: 0;

  flex: 1;
}

.recent-job-info h3 {
  margin: 0;

  color:
    var(--text-primary);

  font-size: 12px;

  font-weight: 800;

  white-space: nowrap;

  overflow: hidden;

  text-overflow: ellipsis;
}

.recent-job-meta {
  margin-top: 4px;

  display: flex;

  align-items: center;

  flex-wrap: wrap;

  gap: 9px;
}

.recent-job-meta span {
  display: flex;

  align-items: center;

  gap: 3px;

  color:
    var(--text-muted);

  font-size: 10px;
}

.recent-job-meta svg {
  color:
    var(--certed-secondary);
}

/* ========================================================
   STATUS
======================================================== */

.status {
  flex-shrink: 0;

  display: inline-flex;

  align-items: center;

  gap: 4px;

  padding: 5px 8px;

  border: 1px solid;

  border-radius: 20px;

  font-size: 9px;

  font-weight: 800;
}

.status.pending {
  background:
    var(--warning-bg);

  border-color:
    rgba(228, 218, 114, .70);

  color:
    var(--warning);
}

.status.shortlisted {
  background:
    var(--certed-primary-soft);

  border-color:
    rgba(149, 100, 221, .35);

  color:
    var(--certed-primary);
}

.status.interview {
  background:
    var(--certed-secondary-soft);

  border-color:
    rgba(149, 100, 221, .40);

  color:
    #7047BC;
}

.status.selected {
  background:
    var(--success-bg);

  border-color:
    #A4DCBD;

  color:
    var(--success);
}

.status.rejected {
  background:
    var(--danger-bg);

  border-color:
    #F2B7B7;

  color:
    var(--danger);
}

.recent-arrow {
  flex-shrink: 0;

  color:
    #C7C0D0;

  transition: .2s ease;
}

.recent-application:hover .recent-arrow {
  color:
    var(--certed-secondary);

  transform:
    translateX(2px);
}

/* ========================================================
   QUICK ACTIONS
======================================================== */

.quick-actions {
  display: flex;

  flex-direction: column;

  gap: 7px;
}

.quick-action {
  width: 100%;

  padding: 11px;

  display: flex;
  align-items: center;

  gap: 9px;

  border: 1px solid #E9E4ED;

  border-radius: 9px;

  background:
    #FCFBFD;

  text-align: left;

  cursor: pointer;

  transition: .2s ease;
}

.quick-action:hover {
  border-color:
    rgba(149, 100, 221, .40);

  background:
    var(--certed-primary-soft);

  transform:
    translateX(2px);
}

.quick-action-icon {
  width: 33px;
  height: 33px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 8px;

  background:
    rgba(149, 100, 221, .13);

  color:
    var(--certed-primary);
}

.quick-action:hover .quick-action-icon {
  background:
    var(--certed-primary);

  color:
    #FFFFFF;
}

.quick-action-content {
  min-width: 0;

  flex: 1;
}

.quick-action-content strong {
  display: block;

  color:
    var(--text-primary);

  font-size: 11px;

  font-weight: 800;
}

.quick-action-content span {
  display: block;

  margin-top: 2px;

  color:
    var(--text-muted);

  font-size: 9px;

  line-height: 1.4;
}

.quick-action > svg {
  color:
    #B8AFBF;

  transition: .2s ease;
}

.quick-action:hover > svg {
  color:
    var(--certed-secondary);

  transform:
    translateX(2px);
}

/* ========================================================
   EMPTY APPLICATIONS
======================================================== */

.empty-applications {
  min-height: 240px;

  display: flex;

  flex-direction: column;

  align-items: center;

  justify-content: center;

  text-align: center;
}

.empty-app-icon {
  width: 55px;
  height: 55px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 14px;

  background:
    var(--certed-primary-soft);

  color:
    var(--certed-primary);

  border:
    1px solid
    rgba(149, 100, 221, .20);
}

.empty-applications h3 {
  margin: 11px 0 3px;

  color:
    var(--text-primary);

  font-size: 14px;
}

.empty-applications p {
  max-width: 330px;

  margin: 0 0 10px;

  color:
    var(--text-muted);

  font-size: 10px;

  line-height: 1.5;
}

.empty-applications button {
  height: 32px;

  display: inline-flex;

  align-items: center;

  gap: 4px;

  padding: 0 11px;

  border: 0;

  border-radius: 7px;

  background:
    var(--certed-primary);

  color:
    #FFFFFF;

  font-size: 10px;

  font-weight: 800;

  cursor: pointer;

  transition: .2s ease;
}

.empty-applications button:hover {
  background:
    var(--certed-secondary);

  transform:
    translateY(-1px);

  box-shadow:
    0 6px 15px
    rgba(62, 15, 141, .18);
}

/* ========================================================
   PROFILE COMPLETION
======================================================== */

.profile-completion {
  max-width: 1500px;

  margin: 0 auto 16px;

  display: flex;

  align-items: center;

  justify-content: space-between;

  gap: 25px;
}

.completion-left {
  display: flex;

  align-items: center;

  gap: 11px;
}

.completion-icon {
  width: 40px;
  height: 40px;

  flex-shrink: 0;

  display: flex;

  align-items: center;

  justify-content: center;

  border-radius: 9px;

  background:
    rgba(228, 218, 114, .28);

  color:
    var(--certed-primary);

  border:
    1px solid
    rgba(228, 218, 114, .55);
}

.completion-left h2 {
  margin: 0;

  color:
    var(--certed-primary);

  font-size: 13px;

  font-weight: 800;
}

.completion-left p {
  max-width: 600px;

  margin: 4px 0 0;

  color:
    var(--text-muted);

  font-size: 10px;

  line-height: 1.45;
}

.completion-right {
  min-width: 270px;

  display: flex;

  align-items: center;

  gap: 13px;
}

.progress-wrapper {
  flex: 1;
}

.progress-label {
  display: flex;

  justify-content: space-between;

  margin-bottom: 5px;
}

.progress-label span {
  color:
    var(--text-muted);

  font-size: 9px;

  font-weight: 700;
}

.progress-label strong {
  color:
    var(--certed-primary);

  font-size: 10px;
}

.progress-bar {
  width: 100%;

  height: 6px;

  overflow: hidden;

  border-radius: 20px;

  background:
    #E4DFE8;
}

.progress-bar span {
  display: block;

  height: 100%;

  border-radius: 20px;

  background:
    linear-gradient(
      90deg,
      var(--certed-primary),
      var(--certed-secondary)
    );

  position: relative;
}

.progress-bar span::after {
  content: "";

  position: absolute;

  right: 0;

  top: 0;

  width: 12px;

  height: 100%;

  border-radius: 20px;

  background:
    var(--certed-accent);
}

.completion-right button {
  height: 32px;

  flex-shrink: 0;

  display: inline-flex;

  align-items: center;

  gap: 4px;

  padding: 0 10px;

  border: 1px solid
    var(--certed-secondary);

  border-radius: 7px;

  background:
    var(--certed-primary);

  color:
    #FFFFFF;

  font-size: 10px;

  font-weight: 800;

  cursor: pointer;

  transition: .2s ease;
}

.completion-right button:hover {
  background:
    var(--certed-secondary);

  transform:
    translateY(-1px);
}

/* ========================================================
   FOOTER NOTE
======================================================== */

.dashboard-footer-note {
  max-width: 1500px;

  margin: 0 auto;

  padding: 9px;

  display: flex;

  align-items: center;

  justify-content: center;

  gap: 6px;

  color:
    var(--text-muted);

  font-size: 9px;

  text-align: center;
}

.dashboard-footer-note svg {
  color:
    var(--certed-secondary);
}

/* ========================================================
   LOADING
======================================================== */

.trainer-dashboard-loading {
  min-height: 100vh;

  display: flex;

  flex-direction: column;

  align-items: center;

  justify-content: center;

  background:
    linear-gradient(
      180deg,
      #FFFFFF,
      #EEEEEE
    );

  text-align: center;

  font-family:
    Inter,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
}

.dashboard-loader {
  width: 60px;
  height: 60px;

  display: flex;

  align-items: center;

  justify-content: center;

  border-radius: 16px;

  background:
    var(--certed-primary-soft);

  color:
    var(--certed-primary);

  border:
    1px solid
    rgba(149, 100, 221, .25);

  box-shadow:
    0 8px 25px
    rgba(62, 15, 141, .10);
}

.trainer-dashboard-loading h3 {
  margin: 16px 0 4px;

  color:
    var(--certed-primary);

  font-size: 17px;
}

.trainer-dashboard-loading p {
  margin: 0;

  color:
    var(--text-muted);

  font-size: 11px;
}

.spin {
  animation:
    dashboardSpin
    1s linear infinite;
}

@keyframes dashboardSpin {

  from {
    transform:
      rotate(0deg);
  }

  to {
    transform:
      rotate(360deg);
  }

}

/* ========================================================
   RESPONSIVE
======================================================== */

@media (max-width: 1200px) {

  .dashboard-stats {
    grid-template-columns:
      repeat(3, minmax(0, 1fr));
  }

  .dashboard-main-grid {
    grid-template-columns:
      1fr;
  }

}

@media (max-width: 800px) {

  .trainer-dashboard {
    padding: 18px;
  }

  .dashboard-header {
    align-items: flex-start;

    flex-direction: column;
  }

  .header-actions {
    width: 100%;
  }

  .profile-mini {
    flex: 1;
  }

  .trainer-hero {
    align-items: flex-start;

    flex-direction: column;
  }

  .hero-profile-btn {
    width: 100%;

    justify-content: center;
  }

  .dashboard-stats {
    grid-template-columns:
      repeat(2, minmax(0, 1fr));
  }

  .profile-completion {
    align-items: flex-start;

    flex-direction: column;
  }

  .completion-right {
    width: 100%;
  }

}

@media (max-width: 560px) {

  .trainer-dashboard {
    padding: 13px;
  }

  .dashboard-header h1 {
    font-size: 21px;
  }

  .dashboard-header p {
    font-size: 10px;
  }

  .breadcrumb {
    font-size: 10px;
  }

  .dashboard-icon {
    width: 40px;
    height: 40px;
  }

  .header-actions {
    flex-wrap: wrap;
  }

  .trainer-hero {
    padding: 15px;
  }

  .hero-profile {
    align-items: flex-start;
  }

  .hero-avatar {
    width: 52px;
    height: 52px;

    border-radius: 13px;

    font-size: 16px;
  }

  .hero-name-row h2 {
    font-size: 16px;
  }

  .hero-details {
    align-items: flex-start;

    flex-direction: column;

    gap: 5px;
  }

  .hero-details span {
    font-size: 10px;
  }

  .dashboard-stats {
    grid-template-columns:
      1fr 1fr;

    gap: 7px;
  }

  .dashboard-stat-card {
    padding: 11px;
  }

  .stat-card-icon {
    width: 30px;
    height: 30px;
  }

  .stat-card-content strong {
    font-size: 18px;
  }

  .stat-card-content span {
    font-size: 9px;
  }

  .dashboard-panel {
    padding: 14px;
  }

  .panel-header h2 {
    font-size: 14px;
  }

  .panel-header p {
    font-size: 10px;
  }

  .recent-application {
    align-items: flex-start;

    flex-wrap: wrap;
  }

  .recent-job-info {
    width:
      calc(100% - 45px);
  }

  .recent-job-info h3 {
    font-size: 11px;
  }

  .recent-job-meta span {
    font-size: 9px;
  }

  .recent-application .status {
    margin-left: 44px;
  }

  .recent-arrow {
    display: none;
  }

  .completion-right {
    align-items: stretch;

    flex-direction: column;
  }

  .completion-right button {
    width: 100%;

    justify-content: center;
  }

}

@media (max-width: 360px) {

  .dashboard-stats {
    grid-template-columns:
      1fr;
  }

}

//trainer-notification
.trainer-quick-action {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px;
  border: 1px solid #E4E0EA;
  border-radius: 10px;
  background: #FCFBFE;
  color: #302A3A;
  text-decoration: none;
  cursor: pointer;
}

.trainer-quick-action-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 10px;
  color: #3E0F8D;
  background: #EFE6FB;
}

.trainer-quick-action-content {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
}

.trainer-quick-action-content strong {
  font-size: 13px;
}

.trainer-quick-action-content span {
  color: #77717F;
  font-size: 11px;
}

.trainer-quick-action:hover {
  border-color: #9564DD;
  background: #F7F2FD;
}

`;